/**
 * applyReverseGeocode.ts — apply a map-click reverse-geocode, guarded against staleness (T-478).
 *
 * A map click / pin move recovers a human address for the clicked point via reverseGeocode()
 * (GET /api/geocode/reverse — T-477 runs it under IntendedUse=Storage). Successive clicks fire
 * successive lookups that can resolve OUT OF ORDER; an earlier response landing after a later one
 * would stamp a stale address on the current location. This helper applies the resolved address
 * only if the request is STILL the latest when it resolves — the same apply-time `isCurrent` guard
 * shape the site-score path uses (see applySiteScore.ts). The caller (MapShell.handlePinMoved) owns
 * a monotonic request-id ref and clears the address synchronously before calling here.
 *
 * Best-effort, exactly like reverseGeocode itself: a null result or a thrown error leaves the
 * cleared state in place (no address shown) and NEVER blocks the parallel scoring flow — reverse
 * geocoding is a nicety on top of the score, not a prerequisite for it.
 */
export interface ApplyReverseGeocodeDeps {
  /** Reverse-geocode the point to a display name, or null on a best-effort miss. */
  reverseGeocode: (lat: number, lng: number) => Promise<string | null>
  /** True iff this lookup is still the latest one when its response resolves (staleness guard). */
  isCurrent: () => boolean
  /** Commit a resolved (map-derived, approximate) address to app state. */
  apply: (address: string) => void
}

/**
 * Run a reverse-geocode for the clicked point and apply the address, guarded against staleness.
 * A superseded lookup (a newer click/search started while this one was in flight) is dropped, a
 * null result is ignored (the address stays cleared), and a rejection is swallowed. Never throws.
 */
export async function resolveAndApplyAddress(
  lat: number,
  lng: number,
  deps: ApplyReverseGeocodeDeps,
): Promise<void> {
  try {
    const name = await deps.reverseGeocode(lat, lng)
    // Apply only if still current AND the lookup actually returned an address — a superseded click
    // must not overwrite the newer location's address, and a miss leaves the cleared state as-is.
    if (!deps.isCurrent() || !name) return
    deps.apply(name)
  } catch {
    // reverseGeocode is already best-effort; a throw here is non-fatal and must not block scoring.
  }
}
